import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Modal, EmptyState } from "@/components/pcp";
import { Plus, Pencil, TrashSimple, Copy, DownloadSimple, FileArrowUp, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { categoria: "Horizontal", descricao: "", unidade: "m²", cor: "", materiais: [], observacoes: "" };

export default function Composicoes() {
  const [items, setItems] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("");
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: i }] = await Promise.all([api.get("/composicoes"), api.get("/insumos")]);
    setItems(c);
    setInsumos(i);
  };
  useEffect(() => { load(); }, []);

  const downloadModelo = async () => {
    const token = localStorage.getItem("pcp_token");
    const r = await fetch(`${API}/composicoes/importacao/modelo`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Erro ao baixar modelo"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "modelo_composicoes.xlsx"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Modelo baixado — abra no Excel e preencha!");
  };

  const submitImport = async () => {
    if (!importFile) { toast.error("Selecione o arquivo"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/composicoes/importacao/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.composicoes_criadas} composição(ões) importada(s)`);
      load();
    } catch (e) {
      toast.error("Erro ao importar planilha");
    } finally { setImporting(false); }
  };

  const save = async () => {
    if (!form.descricao) { toast.error("Preencha a descrição"); return; }
    try {
      if (editId) await api.put(`/composicoes/${editId}`, form);
      else await api.post("/composicoes", form);
      toast.success("Composição salva");
      setModal(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const openEdit = (c) => { setForm({ ...empty, ...c, materiais: c.materiais || [] }); setEditId(c.id); setModal(true); };

  const clone = (c) => {
    setForm({ ...empty, ...c, descricao: `${c.descricao} (cópia)`, materiais: [...(c.materiais || [])] });
    setEditId(null);
    setModal(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir composição?")) return;
    await api.delete(`/composicoes/${id}`);
    toast.success("Composição removida");
    load();
  };

  const addMaterial = () => {
    setForm({ ...form, materiais: [...form.materiais, { insumo_id: "", insumo_nome: "", quantidade_por_unidade: 0, unidade: "" }] });
  };
  const updateMat = (i, patch) => {
    const arr = [...form.materiais];
    arr[i] = { ...arr[i], ...patch };
    if (patch.insumo_id) {
      const ins = insumos.find((x) => x.id === patch.insumo_id);
      if (ins) { arr[i].insumo_nome = ins.nome; arr[i].unidade = ins.unidade; }
    }
    setForm({ ...form, materiais: arr });
  };
  const rmMat = (i) => setForm({ ...form, materiais: form.materiais.filter((_, ix) => ix !== i) });

  const filtered = items.filter((c) =>
    !filter || c.descricao.toLowerCase().includes(filter.toLowerCase()) || c.categoria.toLowerCase().includes(filter.toLowerCase())
  );

  const grouped = filtered.reduce((acc, c) => {
    (acc[c.categoria] = acc[c.categoria] || []).push(c);
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        testid="page-composicoes"
        title="Composições / Fichas Técnicas"
        subtitle={`${items.length} composições · Cálculo automático de materiais por serviço`}
        actions={
          <>
            <input className="input" placeholder="Buscar…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ minWidth: 220 }} />
            <button className="btn btn-outline" onClick={downloadModelo} data-testid="btn-modelo-comp">
              <DownloadSimple size={16} weight="bold" /> Baixar modelo
            </button>
            <button className="btn btn-outline" onClick={() => { setImportFile(null); setImportResult(null); setImportModal(true); }} data-testid="btn-import-comp">
              <FileArrowUp size={16} weight="bold" /> Importar Excel
            </button>
            <button className="btn btn-primary" onClick={() => { setForm(empty); setEditId(null); setModal(true); }} data-testid="btn-new-comp">
              <Plus size={16} weight="bold" /> Nova composição
            </button>
          </>
        }
      />
      <div className="page-body">
        {items.length === 0 ? (
          <EmptyState title="Nenhuma composição" />
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} style={{ marginBottom: 32 }}>
              <div className="overline mb-3">{cat} · {list.length}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
                {list.map((c) => (
                  <div key={c.id} className="pcp-card" style={{ padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div className="font-display font-bold" style={{ fontSize: "1rem", lineHeight: 1.2 }}>{c.descricao}</div>
                        <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
                          <span className="chip">{c.unidade}</span>
                          {c.cor && <span className="chip">{c.cor}</span>}
                          {c.sistema && <span className="chip" style={{ background: "#FFCC00", color: "#0A0A0A" }}>Padrão</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 2 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => clone(c)} title="Duplicar"><Copy size={12} weight="bold" /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}><Pencil size={12} weight="bold" /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remove(c.id)}><TrashSimple size={12} weight="bold" color="#DC2626" /></button>
                      </div>
                    </div>
                    <div className="divider" style={{ margin: "8px 0" }} />
                    <div className="overline mb-2">Consumo por {c.unidade}</div>
                    {c.materiais?.length === 0 ? (
                      <div style={{ color: "#71717A", fontSize: "0.8rem" }}>Sem materiais</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {c.materiais.map((m, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
                            <span style={{ color: "#27272A" }}>{m.insumo_nome}</span>
                            <span className="font-mono font-semibold">{m.quantidade_por_unidade} {m.unidade}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Editar composição" : "Nova composição"} size="xl">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <F label="Categoria">
            <select className="select" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
              {["Vertical", "Horizontal", "Defensas", "Tachas", "Demarcação", "Outros"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </F>
          <F label="Unidade base">
            <select className="select" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })}>
              {["m²", "ml", "un", "kg"].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </F>
          <F label="Cor">
            <input className="input" value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })} />
          </F>
        </div>
        <F label="Descrição *"><input className="input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="comp-descricao" /></F>

        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div className="overline">Materiais por {form.unidade}</div>
            <button className="btn btn-outline btn-sm" onClick={addMaterial} data-testid="btn-add-mat"><Plus size={14} weight="bold" /> Material</button>
          </div>
          {form.materiais.length === 0 ? (
            <div style={{ padding: 16, background: "#FAFAFA", border: "1px dashed #D4D4D8", borderRadius: 3, color: "#71717A", textAlign: "center", fontSize: "0.85rem" }}>
              Adicione ao menos um material para o cálculo automático.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {form.materiais.map((m, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 40px", gap: 8, alignItems: "flex-end" }}>
                  <div>
                    {i === 0 && <div className="label">Insumo</div>}
                    <select className="select" value={m.insumo_id || ""} onChange={(e) => updateMat(i, { insumo_id: e.target.value })}>
                      <option value="">— Selecione —</option>
                      {insumos.map((ins) => <option key={ins.id} value={ins.id}>{ins.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    {i === 0 && <div className="label">Qtd/{form.unidade}</div>}
                    <input className="input" type="number" step="0.001" value={m.quantidade_por_unidade} onChange={(e) => updateMat(i, { quantidade_por_unidade: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    {i === 0 && <div className="label">Unid.</div>}
                    <input className="input" value={m.unidade} onChange={(e) => updateMat(i, { unidade: e.target.value })} />
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => rmMat(i)} style={{ marginBottom: 0 }}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-comp">Salvar composição</button>
        </div>
      </Modal>

      <Modal open={importModal} onClose={() => setImportModal(false)} title="Importar Composições via Excel" size="lg">
        <div style={{ padding: 16, background: "#FFFCEA", border: "1px solid #FFCC00", borderRadius: 3, marginBottom: 20 }}>
          <div className="overline mb-2">Como funciona</div>
          <ol style={{ paddingLeft: 20, margin: 0, fontSize: "0.85rem", color: "#27272A", lineHeight: 1.7 }}>
            <li>Baixe o <strong>modelo Excel</strong> com abas de exemplo e instruções detalhadas.</li>
            <li>Preencha uma linha por <strong>insumo</strong> — repita a mesma composição em várias linhas para adicionar vários insumos.</li>
            <li>Salve o arquivo e faça upload aqui.</li>
            <li>Insumos novos são cadastrados automaticamente no almoxarifado (com saldo 0).</li>
            <li>Composições com nome já existente <strong>são ignoradas</strong>.</li>
          </ol>
          <button className="btn btn-dark btn-sm" style={{ marginTop: 12 }} onClick={downloadModelo}>
            <DownloadSimple size={14} weight="bold" /> Baixar modelo
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="label">Arquivo Excel (.xlsx) preenchido</label>
          <input
            type="file"
            accept=".xlsx"
            className="input"
            onChange={(e) => setImportFile(e.target.files[0])}
            data-testid="import-comp-file"
          />
        </div>

        {importResult && (
          <div style={{ padding: 16, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#34C759", fontWeight: 700, marginBottom: 8 }}>
              <CheckCircle size={18} weight="bold" /> Importação concluída
            </div>
            <div className="font-mono text-sm" style={{ lineHeight: 1.7 }}>
              <div>Composições criadas: <strong>{importResult.composicoes_criadas}</strong></div>
              <div>Insumos criados automaticamente: <strong>{importResult.insumos_criados_automaticamente}</strong></div>
              {importResult.composicoes_ignoradas.length > 0 && (
                <div style={{ color: "#F59E0B", marginTop: 6 }}>
                  {importResult.composicoes_ignoradas.length} ignoradas (nome já existente):
                  <ul style={{ margin: "4px 0 0 20px", fontSize: "0.8rem" }}>
                    {importResult.composicoes_ignoradas.slice(0, 10).map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}
              {importResult.erros.length > 0 && (
                <div style={{ color: "#DC2626", marginTop: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Warning size={14} weight="bold" /> {importResult.erros.length} erros:</div>
                  <ul style={{ margin: "4px 0 0 20px", fontSize: "0.75rem" }}>
                    {importResult.erros.slice(0, 15).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-outline" onClick={() => setImportModal(false)}>Fechar</button>
          <button className="btn btn-primary" onClick={submitImport} disabled={!importFile || importing} data-testid="btn-submit-import-comp">
            <FileArrowUp size={16} weight="bold" /> {importing ? "Importando…" : "Importar planilha"}
          </button>
        </div>
      </Modal>
    </>
  );
}
function F({ label, children }) { return <div><div className="label">{label}</div>{children}</div>; }
